/**
 * ConnectionLostModal Component
 * Player-facing modal shown when the Realtime channel goes down.
 * Debounced to 2 s so quick blips don't flash a scary modal.
 */

import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

interface ConnectionLostModalProps {
  /** Whether the channel is currently connected */
  isConnected: boolean;
  /** Whether the hook is currently auto-retrying */
  isReconnecting: boolean;
  /** Number of manual reconnect attempts already made */
  manualRetryCount: number;
  /** Whether there is an active session to reconnect to */
  hasSession: boolean;
  /** Called when the user presses "Opnieuw verbinden" */
  onReconnect: () => void;
}

export const ConnectionLostModal: React.FC<ConnectionLostModalProps> = ({
  isConnected,
  isReconnecting,
  manualRetryCount,
  hasSession,
  onReconnect,
}) => {
  const colors = useThemeColors();
  const [visible, setVisible] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isConnected && hasSession) {
      // Show after 2 s debounce
      debounceRef.current = setTimeout(() => setVisible(true), 2000);
    } else {
      // Hide immediately when connected
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setVisible(false);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isConnected, hasSession]);

  if (!visible) return null;

  const showHint = manualRetryCount >= 3;

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent>
      <View className="flex-1 items-center justify-center bg-black/80 px-6">
        <View
          className="w-full max-w-md rounded-3xl p-8 items-center"
          style={{ backgroundColor: colors.surface }}
        >
          <View
            className="w-24 h-24 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: "#EF444420" }}
          >
            <Ionicons name="cloud-offline" size={56} color="#EF4444" />
          </View>

          <Text
            className="text-2xl font-bold text-center mb-2"
            style={{ color: colors.text }}
          >
            Verbinding verloren
          </Text>

          <Text
            className="text-center text-base mb-6"
            style={{ color: colors.textSecondary }}
          >
            We zijn de verbinding met de quiz kwijt.
            {isReconnecting
              ? " We proberen automatisch opnieuw te verbinden."
              : " Druk op de knop om opnieuw te verbinden."}
          </Text>

          {isReconnecting ? (
            <View className="flex-row items-center py-4">
              <ActivityIndicator size="small" color={colors.primary} />
              <Text className="ml-3" style={{ color: colors.textSecondary }}>
                Bezig met opnieuw verbinden…
              </Text>
            </View>
          ) : (
            <TouchableOpacity
              onPress={onReconnect}
              className="w-full py-4 rounded-xl items-center"
              style={{ backgroundColor: colors.primary }}
              accessibilityLabel="Opnieuw verbinden"
              accessibilityRole="button"
            >
              <View className="flex-row items-center">
                <Ionicons name="refresh" size={20} color="white" />
                <Text className="ml-2 font-semibold text-white text-base">
                  Opnieuw verbinden
                </Text>
              </View>
            </TouchableOpacity>
          )}

          {showHint && (
            <View
              className="flex-row items-center rounded-xl px-4 py-3 mt-4"
              style={{ backgroundColor: "#F59E0B15" }}
            >
              <Ionicons name="information-circle" size={18} color="#F59E0B" />
              <Text className="flex-1 ml-2 text-sm" style={{ color: colors.text }}>
                Controleer je internetverbinding en probeer het opnieuw.
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default ConnectionLostModal;
